import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import { Emitter } from '@patsnap/tahiti-core';
import { CheckStatisticsApi } from '@/domains/proof-reading/apis/check-statistics-api';
import { formatNumber } from '@/utils/format-number';
import type { CheckStatistics, CheckStatisticsService } from '../check-statistics-service';

@injectable()
export class CheckStatisticsServiceImpl implements CheckStatisticsService {
    private _statistics: CheckStatistics = {
        issues: '0',
        scannedWord: '0',
        spendTime: '0',
    };

    private _isLoading = false;

    private readonly _onStatisticsChangeEmitter = new Emitter<CheckStatistics>();
    readonly onStatisticsChange = this._onStatisticsChangeEmitter.event;

    private readonly _onLoadingChangeEmitter = new Emitter<boolean>();
    readonly onLoadingChange = this._onLoadingChangeEmitter.event;

    constructor(
        @inject(CheckStatisticsApi)
        private readonly _checkStatisticsApi: CheckStatisticsApi,
    ) {}

    getStatistics(): CheckStatistics {
        return { ...this._statistics };
    }

    getIsLoading(): boolean {
        return this._isLoading;
    }

    async loadStatistics(taskId: string): Promise<void> {
        this._setLoading(true);

        try {
            const result = await this._checkStatisticsApi.checkStatistics({ taskId });
            const newStatistics: CheckStatistics = {
                issues: formatNumber(result.issues),
                scannedWord: formatNumber(result.scanned_word),
                spendTime: formatNumber(result.spend_time),
            };

            this._setStatistics(newStatistics);
        } catch (error) {
            console.warn('Failed to load check statistics', error);
            throw error;
        } finally {
            this._setLoading(false);
        }
    }

    private _setStatistics(statistics: CheckStatistics): void {
        this._statistics = { ...statistics };
        this._onStatisticsChangeEmitter.fire(this._statistics);
    }

    private _setLoading(isLoading: boolean): void {
        if (this._isLoading !== isLoading) {
            this._isLoading = isLoading;
            this._onLoadingChangeEmitter.fire(this._isLoading);
        }
    }
}
